// ─── suggestConsultationBriefing.js ────────────────────────────────────────
// Aide à la rédaction pour Anissa (praticienne) : juste avant un rendez-vous,
// à partir de la période écoulée DEPUIS la dernière consultation (tendances des
// ressentis cliente + commentaires libres + évolution du poids), l'IA PROPOSE
// un BROUILLON de synthèse pré-consultation. Anissa relit, édite et valide.
// L'IA propose, Anissa décide.
//
// GARDE-FOUS (stricts) :
// - Périmètre NUTRITIONNEL / accompagnement uniquement. JAMAIS un diagnostic,
//   jamais une interprétation médicale.
// - Consolide UNIQUEMENT ce qui est fourni (tendances, commentaires, delta de
//   poids). N'invente AUCUNE donnée, ne suppose aucun ressenti absent.
// - Défaut conservateur : formulations prudentes, jamais de certitude sur une
//   cause. Aucune décision clinique codée en dur ici.
// - Sortie = BROUILLON pré-rempli dans un textarea. Jamais appliqué ni
//   sauvegardé automatiquement.
//
// Calqué sur suggestGlobalSynthesis.js (même esprit, entrée différente).
// Réutilise le client Claude unifié (services/anthropic.js — jamais d'appel
// direct à /api/claude, jamais de modèle codé en dur).

import { callClaude, ClaudeApiError } from './anthropic';
import {
  assertMeetingAiSafetySnapshot,
  normalizeGeneratedMeetingBriefing,
} from './consultationMeetingWorkspace';
import { parseMealPrepOverride } from './mealPrep';
import { MEAL_STRUCTURES } from './prompts/nutrition/mealStructure.fr';

const SYSTEM_PROMPT = `Tu aides Anissa à préparer un rendez-vous avec une cliente (Anissa n'est ni médecin ni biologiste). À partir de ce qui s'est passé DEPUIS la dernière consultation (tendances de ses ressentis, ses commentaires, l'évolution de son poids), tu rédiges un BROUILLON de synthèse pré-consultation. Ton rôle : mettre au propre la période dans un langage de nutritionniste simple, pour qu'Anissa arrive au rendez-vous au clair sur l'essentiel.

REGISTRE — LANGAGE NUTRITIONNISTE SIMPLE (le point le plus important)
- Écris comme une nutritionniste parle, pas comme un compte-rendu médical ou de laboratoire.
- Reste concret : ce que la personne a ressenti (digestion, fatigue, énergie, faim) et comment ça a évolué sur la période.
- Phrases courtes et accessibles.

INTERDIT — JARGON MÉDICAL / BIOLOGIQUE / DIAGNOSTIC
- N'emploie PAS de termes techniques (marqueurs, ratios, noms de maladies, dosages).
- Tu ne poses JAMAIS de diagnostic, tu ne nommes aucune maladie ni traitement. La décision clinique appartient à Anissa seule.

CE QUE TU AS LE DROIT DE FAIRE
- Consolider et relier UNIQUEMENT ce qui est fourni : les tendances par axe, les commentaires de la cliente, le delta de poids.
- Faire ressortir 1 à 3 points d'attention concrets pour le rendez-vous (ce qui va mieux, ce qui reste à surveiller, une question à poser).

INTERDICTIONS ABSOLUES
- N'invente AUCUNE donnée, AUCUN ressenti, AUCUNE valeur absente. Si une info manque (ex. aucune pesée), tu ne la supposes pas — au besoin tu le dis sobrement ("pas de pesée sur la période").
- Ne recopie pas les commentaires un à un : tu SYNTHÉTISES.

TON (défaut prudent)
- Formulations prudentes : "semble aller vers", "à confirmer avec elle", "piste à creuser". Jamais de certitude sur une cause.
- Vocabulaire d'accompagnement : "soutenir", "favoriser", "prioriser", "rééquilibrer". Évite "soigne", "guérit", "traite", "détecte".

FORMAT
- Français, tutoiement, ~3 à 6 lignes, texte suivi ou courtes puces. Concis.
- Zéro emoji. Réponds UNIQUEMENT avec le texte, sans titre, sans préambule ("Voici…"), sans markdown, sans guillemets englobants.

RAPPEL : c'est un BROUILLON qu'Anissa relit, ajuste et valide avant le rendez-vous. Reste simple, sobre et fidèle à ce qui s'est passé sur la période.`;

/**
 * Construit le message utilisateur (résumé de la période) envoyé à l'IA.
 * Pure : ne dépend d'aucun I/O. Testable isolément.
 *
 * @param {object} data
 * @param {string} [data.periodLabel]  - ex. « du 3 juin au 8 juillet »
 * @param {object} [data.weight]       - { start, end, deltaKg } ou null
 * @param {Array}  [data.trends]       - [{ axis, label, dominant, count }]
 * @param {Array}  [data.comments]     - [{ date, text }]
 * @returns {string} le prompt utilisateur
 */
export function buildConsultationBriefingPrompt(data = {}) {
  const { periodLabel, weight, trends, comments } = data;
  const lines = [];

  lines.push(`=== PÉRIODE ===`);
  lines.push(periodLabel ? `Période couverte : ${periodLabel} (depuis la dernière consultation).` : 'Période récente depuis la dernière consultation.');
  lines.push('');

  // ─── Poids ───────────────────────────────────────────────────────────
  lines.push('=== ÉVOLUTION DU POIDS ===');
  if (weight && typeof weight.start === 'number' && typeof weight.end === 'number') {
    if (Array.isArray(weight.points) && weight.points.length === 1) {
      lines.push(`Poids renseigné : ${weight.end} kg (évolution non calculable avec une seule mesure).`);
    } else if (typeof weight.deltaKg === 'number') {
      const sign = weight.deltaKg > 0 ? '+' : '';
      lines.push(`Début : ${weight.start} kg → Fin : ${weight.end} kg (${sign}${weight.deltaKg} kg sur la période).`);
    } else {
      lines.push(`Poids renseigné : ${weight.end} kg (évolution non calculable).`);
    }
  } else {
    lines.push('Aucune pesée enregistrée sur la période.');
  }
  lines.push('');

  // ─── Tendances des ressentis ──────────────────────────────────────────
  lines.push('=== TENDANCES DES RESSENTIS (par axe, valeur dominante) ===');
  const t = Array.isArray(trends) ? trends.filter(Boolean) : [];
  if (t.length) {
    t.forEach((tr) => {
      const total = typeof tr.total === 'number' ? tr.total : tr.count;
      if (Array.isArray(tr.tiedValues) && tr.tiedValues.length > 1) {
        lines.push(`- ${tr.label} : réponses ex æquo « ${tr.tiedValues.join(' » / « ')} » (${tr.count}/${total} chacune).`);
      } else {
        lines.push(`- ${tr.label} : « ${tr.dominant} » (${tr.count}/${total} ressentis).`);
      }
    });
  } else {
    lines.push('Aucun ressenti renseigné sur la période.');
  }
  lines.push('');

  // ─── Commentaires de la cliente ───────────────────────────────────────
  lines.push('=== COMMENTAIRES DE LA CLIENTE ===');
  const c = Array.isArray(comments) ? comments.filter((x) => x && x.text) : [];
  if (c.length) {
    c.forEach((cm) => {
      const day = cm.date ? new Date(cm.date).toLocaleDateString('fr-CH', { day: 'numeric', month: 'short' }) : '';
      lines.push(day ? `- (${day}) ${cm.text}` : `- ${cm.text}`);
    });
  } else {
    lines.push('Aucun commentaire libre laissé par la cliente sur la période.');
  }
  lines.push('');

  lines.push('=== TÂCHE ===');
  lines.push(
    "Rédige un BROUILLON de synthèse pré-consultation (~3-6 lignes), en langage SIMPLE de nutritionniste, qui consolide UNIQUEMENT ces éléments et fait ressortir 1 à 3 points d'attention concrets pour le rendez-vous (ce qui va mieux, ce qui reste à surveiller, une question à poser). N'invente rien. Reste prudente et fidèle à ce qui s'est passé.",
  );

  return lines.join('\n');
}

/**
 * Indique s'il y a assez de matière pour proposer un briefing.
 * Vrai si au moins un ressenti (tendance) OU un commentaire OU une pesée.
 *
 * @param {object} data - { weight, trends[], comments[] }
 * @returns {boolean}
 */
export function hasEnoughForBriefing(data = {}) {
  const { weight, trends, comments } = data;
  const hasTrend = Array.isArray(trends) && trends.some((t) => t && t.count > 0);
  const hasComment = Array.isArray(comments) && comments.some((c) => c && c.text && String(c.text).trim());
  const hasWeight = !!(weight && typeof weight.start === 'number' && typeof weight.end === 'number');
  return hasTrend || hasComment || hasWeight || buildProgramMeetingFacts(data?.programme).length > 0;
}

/**
 * Appelle Claude pour proposer un BROUILLON de synthèse pré-consultation.
 * Ne sauvegarde rien : renvoie juste le texte proposé, que l'UI pré-remplit
 * dans le textarea qu'Anissa édite ensuite.
 *
 * @param {object} data - { periodLabel, weight, trends[], comments[] }
 * @returns {Promise<string>} le brouillon de synthèse
 * @throws {Error} message clair si pas de matière / erreur IA / réponse vide
 */
export async function suggestConsultationBriefing(data = {}) {
  assertMeetingAiSafetySnapshot(data?.safetySnapshot);
  if (!hasEnoughForBriefing(data)) {
    throw new Error(
      "Pas encore assez d'éléments sur la période (aucun ressenti, commentaire ni pesée) pour proposer une synthèse.",
    );
  }

  const user = buildConsultationBriefingPrompt(data);

  let text;
  try {
    text = await callClaude({
      system: SYSTEM_PROMPT,
      user,
      maxTokens: 800,
      // Filet de sécurité compliance en plus du guard vocabulaire injecté par défaut.
      sanitizeOutput: true,
    });
  } catch (err) {
    if (err instanceof ClaudeApiError) {
      throw new Error(`La proposition a échoué : ${err.message}`);
    }
    throw new Error(`La proposition a échoué : ${err?.message || err}`);
  }

  const draft = (text || '').trim();
  if (!draft) {
    throw new Error("La préparation n’a renvoyé aucune proposition. Réessaie.");
  }
  return draft;
}

const MEETING_SYSTEM_PROMPT = `Tu sélectionnes des questions pour préparer un rendez-vous.

Tu n'écris aucun texte libre. Tu choisis uniquement :
- fact_ref : une référence présente exactement dans facts ;
- intent : clarify, context ou daily_experience.

Tu ne poses aucun diagnostic, ne déduis aucune cause et ne proposes aucune action. Le SaaS rendra lui-même les phrases avec des modèles neutres. Les questions restent décochées jusqu'au choix explicite d'Anissa.

Réponds uniquement avec cet objet JSON exact, sans markdown ni autre clé :
{
  "questions": [
    { "fact_ref": "référence exacte", "intent": "clarify" }
  ]
}

Retourne au maximum 7 paires uniques. N'invente aucune référence ni intention.`;

const MEETING_TREND_FACTS = Object.freeze({
  digestion: { label: 'Digestion', values: new Set(['Mieux', 'Pareil', 'Pire']) },
  fatigue: { label: 'Fatigue', values: new Set(['Mieux', 'Pareil', 'Pire']) },
  energie: { label: 'Énergie', values: new Set(['Bonne', 'OK', 'Basse']) },
  faim: { label: 'Faim', values: new Set(['Pas assez', 'OK', 'Trop']) },
});

const MEAL_STRUCTURE_LABELS = new Map(MEAL_STRUCTURES.map((item) => [item.id, item.label]));

/**
 * Réduit le programme actif à trois repères logistiques explicitement autorisés.
 * Aucun texte du plan, nom de phase, complément, dose ou recommandation n'est
 * lu ni transmis à l'IA.
 */
export function buildProgramMeetingFacts(programme = null) {
  if (!programme || typeof programme !== 'object' || Array.isArray(programme)) return [];
  const facts = [];
  const overrides = programme.editorial_overrides || programme.editorialOverrides || {};
  const mealStructureId = typeof overrides?.meal_structure === 'string'
    ? overrides.meal_structure
    : '';
  const mealStructureLabel = MEAL_STRUCTURE_LABELS.get(mealStructureId);
  if (mealStructureLabel) {
    facts.push({ ref: 'program:meal_structure', label: `Structure validée : ${mealStructureLabel}` });
  }

  const mealPrep = parseMealPrepOverride(overrides?.meal_prep);
  if (mealPrep?.status === 'validated') {
    facts.push({
      ref: 'program:meal_prep',
      label: `Meal prep validé par Anissa : ${mealPrep.enabled === true ? 'oui' : 'non'}`,
    });
  }

  const protocol = programme.protocol_phases || programme.protocolPhases;
  const phases = Array.isArray(protocol?.phases) ? protocol.phases : [];
  if (phases.length > 0 && phases.length <= 20) {
    const activeId = programme.active_phase_id || programme.activePhaseId || null;
    let activeIndex = activeId ? phases.findIndex((phase) => phase?.id === activeId) : -1;
    if (activeIndex < 0) {
      const activeIndexes = phases
        .map((phase, index) => (phase?.status === 'active' ? index : -1))
        .filter((index) => index >= 0);
      if (activeIndexes.length === 1) activeIndex = activeIndexes[0];
    }
    if (activeIndex >= 0) {
      facts.push({
        ref: 'program:phase_progress',
        label: `Parcours validé : phase ${activeIndex + 1} sur ${phases.length}`,
      });
    }
  }
  return facts;
}

export function buildConsultationMeetingFacts(data = {}) {
  const facts = [];
  const points = Array.isArray(data?.weight?.points) ? data.weight.points.length : 0;
  const delta = Number(data?.weight?.deltaKg);
  if (points === 1 && Number.isFinite(Number(data?.weight?.end))) {
    facts.push({
      ref: 'weight:period',
      label: `Poids : 1 mesure renseignée (${Number(data.weight.end)} kg), évolution non calculable`,
    });
  } else if (points > 1 && points <= 366 && Number.isFinite(delta)) {
    const signedDelta = `${delta > 0 ? '+' : ''}${delta}`;
    facts.push({
      ref: 'weight:period',
      label: `Poids : ${points} mesure${points > 1 ? 's' : ''} renseignée${points > 1 ? 's' : ''}, évolution numérique ${signedDelta} kg`,
    });
  }

  for (const trend of Array.isArray(data?.trends) ? data.trends : []) {
    const config = MEETING_TREND_FACTS[trend?.axis];
    const count = Number(trend?.count);
    const total = Number(trend?.total);
    const tiedValues = Array.isArray(trend?.tiedValues) ? trend.tiedValues : [];
    const validTie = tiedValues.length > 1 && tiedValues.every((value) => config?.values.has(value));
    if (
      !config
      || (!config.values.has(trend?.dominant) && !validTie)
      || !Number.isInteger(count)
      || !Number.isInteger(total)
      || count < 1
      || total < count
      || total > 366
    ) continue;
    facts.push(validTie ? {
      ref: `trend:${trend.axis}`,
      label: `${config.label} : réponses ex æquo « ${tiedValues.join(' » / « ')} » (${count}/${total} chacune)`,
    } : {
      ref: `trend:${trend.axis}`,
      label: `${config.label} : réponse la plus fréquente « ${trend.dominant} » (${count}/${total})`,
    });
  }

  const commentCount = Array.isArray(data?.comments)
    ? data.comments.filter((comment) => typeof comment?.text === 'string' && comment.text.trim()).length
    : 0;
  if (commentCount > 0) {
    facts.push({
      ref: 'context:comments',
      label: `${Math.min(commentCount, 366)} commentaire${commentCount > 1 ? 's' : ''} libre${commentCount > 1 ? 's' : ''} disponible${commentCount > 1 ? 's' : ''} dans le dossier SaaS`,
    });
  }
  facts.push(...buildProgramMeetingFacts(data?.programme));
  return facts.slice(0, 10);
}

export function buildConsultationMeetingPrompt(data = {}) {
  // Minimisation et contrat fermé : aucun commentaire, date, nom ni texte libre
  // n'est transmis. Claude ne voit que le catalogue structuré ci-dessous.
  return JSON.stringify({
    version: 1,
    facts: buildConsultationMeetingFacts(data),
    allowed_intents: ['clarify', 'context', 'daily_experience'],
  });
}

/**
 * Autorité locale d'une requête IA. Elle ne quitte pas le navigateur, mais
 * invalide une réponse si la cliente, les repères programme ou le snapshot de
 * ressentis/pesées ont changé pendant l'appel.
 */
export function buildConsultationMeetingAuthorityFingerprint(clientId, data = {}) {
  const feedbackSnapshot = {
    trends: Array.isArray(data?.trends)
      ? data.trends.map((item) => ({
        axis: item?.axis,
        dominant: item?.dominant,
        tiedValues: Array.isArray(item?.tiedValues) ? item.tiedValues : [],
        count: item?.count,
        total: item?.total,
      }))
      : [],
    comments: Array.isArray(data?.comments)
      ? data.comments.map((item) => ({ date: item?.date || null, text: item?.text || '' }))
      : [],
    weight: data?.weight
      ? {
        start: data.weight.start,
        end: data.weight.end,
        deltaKg: data.weight.deltaKg,
        points: data.weight.points?.length || 0,
      }
      : null,
  };
  return JSON.stringify({
    version: 1,
    client_id: String(clientId || ''),
    program_facts: buildProgramMeetingFacts(data?.programme),
    feedback_snapshot: feedbackSnapshot,
  });
}

export async function suggestConsultationMeeting(data = {}) {
  assertMeetingAiSafetySnapshot(data?.safetySnapshot);
  if (!hasEnoughForBriefing(data)) {
    throw new Error(
      "Pas encore assez d'éléments fiables sur la période pour préparer les questions du rendez-vous.",
    );
  }

  let result;
  const facts = buildConsultationMeetingFacts(data);
  if (facts.length === 0) {
    throw new Error("Pas encore assez d'éléments structurés fiables pour préparer les questions du rendez-vous.");
  }
  try {
    result = await callClaude({
      system: MEETING_SYSTEM_PROMPT,
      user: buildConsultationMeetingPrompt(data),
      maxTokens: 600,
      parseJson: true,
    });
  } catch (err) {
    if (err instanceof ClaudeApiError) {
      throw new Error(`La préparation a échoué : ${err.message}`);
    }
    throw new Error(`La préparation a échoué : ${err?.message || err}`);
  }
  return normalizeGeneratedMeetingBriefing(result, facts);
}
